import { type ApiProblem, call } from '@rental/contracts/client'
import { Button, Field, Notice } from '@rental/ui'
import { useNavigate } from '@tanstack/react-router'
import { type FormEvent, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useAccess, useSession } from '../access-context'
import { api } from '../api'
import { PageHeader } from '../components/PageHeader'
import { ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'

/** A PIN is chosen just after a password sign-in, never from a PIN-unlocked screen. */
export function SetPinScreen() {
  const { messages: m } = useLocale()
  const { transition, takeOver, lock } = useAccess()
  const session = useSession()
  const navigate = useNavigate()
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  // Said under the field it is about, which then takes the focus so it is read.
  const [error, setError] = useState<{ field: 'pin' | 'confirm'; text: string } | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [busy, setBusy] = useState(false)
  const pinField = useRef<HTMLInputElement>(null)
  const confirmField = useRef<HTMLInputElement>(null)

  if (!session.canSetPin) {
    return (
      <>
        <PageHeader title={m.pin.title} />
        <section className="glass-pane flex max-w-xl flex-col gap-4 rounded-card p-4 sm:p-6">
          <Notice tone="info">
            <p>{m.pin.needsPassword}</p>
          </Notice>
          <Button variant="staff" onClick={() => void lock('locked')}>
            {m.nav.lock}
          </Button>
        </section>
      </>
    )
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setProblem(null)
    if (!/^\d{6}$/.test(pin)) {
      flushSync(() => setError({ field: 'pin', text: m.pin.format }))
      pinField.current?.focus()
      return
    }
    if (pin !== confirm) {
      flushSync(() => setError({ field: 'confirm', text: m.pin.mismatch }))
      confirmField.current?.focus()
      return
    }
    setError(null)
    setBusy(true)
    const result = await call(api.PUT('/v1/staff/me/pin', { body: { pin } }))
    setBusy(false)
    if (result.ok) {
      transition({ state: 'unlocked', session: result.data })
      void navigate({ to: '/' })
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
  }

  const digits = (value: string) => value.replace(/\D/g, '').slice(0, 6)
  return (
    <>
      <PageHeader title={m.pin.title} intro={m.pin.intro} />
      <form
        onSubmit={submit}
        className="glass-pane flex max-w-xl flex-col gap-4 rounded-card p-4 sm:p-6"
        noValidate
      >
        <Field label={m.pin.pin} required error={error?.field === 'pin' ? error.text : null}>
          {(control) => (
            <StaffInput
              {...control}
              ref={pinField}
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              className="figures"
              value={pin}
              onChange={(event) => setPin(digits(event.target.value))}
            />
          )}
        </Field>
        <Field
          label={m.pin.confirm}
          required
          error={error?.field === 'confirm' ? error.text : null}
        >
          {(control) => (
            <StaffInput
              {...control}
              ref={confirmField}
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              className="figures"
              value={confirm}
              onChange={(event) => setConfirm(digits(event.target.value))}
            />
          )}
        </Field>
        <ProblemNotice problem={problem} />
        <div className="flex flex-wrap gap-3">
          <Button type="submit" variant="staff" busy={busy}>
            {m.pin.submit}
          </Button>
          {session.pinMissing ? null : (
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => void navigate({ to: '/' })}
            >
              {m.pin.later}
            </Button>
          )}
        </div>
      </form>
    </>
  )
}
